<template>
  <div class="row">
    <div class="card col-xl-10 col-xxl-12">
      <div class="card-header">
        <h5>{{ `№ ${settings.id} ${auctionContent.title} ` }}</h5>
      </div>
      <div class="card-body">
        <ul
          style="list-style-type: none"
        >
          <li v-if="isStarted">
            <strong class="fw-bold">{{ `${$t('auction.expires_in')}: ` }}</strong>{{ settings.activeTo| dateFormatter('DD-MM-YY HH:mm') }}
          </li>
          <li v-else>
            <strong>{{ $t('auction.date_open') }}: </strong>
            {{ settings.activeFrom | dateFormatter('DD-MM-YY HH:mm') }}
          </li>
          <li v-if="auctionContent.bidList[0]">
            <strong>{{ `${$t('auction.leading')}: ` }}</strong>{{ $l(auctionContent.bidList[0] ? auctionContent.bidList[0].sellerFullName : '') }}
          </li>
          <li>
            <strong>{{ $t('auction.current_price') }}: </strong>{{ (lastBidPrice) | money }}
          </li>
        </ul>
        <a-divider />
        <div class="row">
          <div class="col-6">
            <h6>{{ $t('auction.lot_content') }}</h6>
            <ul style="list-style-type: none">
              <li
                v-for="promotion in auctionContent.lotPromotions"
                :key="promotion.promotionType.name"
              >
                <strong>{{ getAuctionTypeTitle(promotion.promotionType.name) }}: </strong>
                {{ `${promotion.places} ${$t('common.pcs')}` }}
              </li>
            </ul>
          </div>
          <div class="col-6">
            <h6>{{ $t('auction.lot_sections') }}</h6>
            <a-tree
              :tree-data="sectionsTree"
            />
          </div>
        </div>
        <div v-if="user.role === 'SELLER' && auctionContent.status === 'STARTED'">
          <div
            class="d-flex align-items-center justify-content-end"
          >
            <label
              class="mr-3 mb-0"
              for="bid-input"
            >
              {{ $t('auction.your_bid') }}
            </label>
            <b-input-group
              style="width: auto"
              class="mr-3"
              :append="$t('common.UZS')"
            >
              <b-input
                id="bid-input"
                v-model="sellerBid"
                type="number"
                number
                :min="(lastBidPrice + settings.minimumStep) / 100"
                :step="settings.minimumStep / 100"
                :formatter="bidFormatter"
                lazy-formatter
              />
            </b-input-group>
            <b-button @click="makeBid">
              {{ $t('auction.accept_bid') }}
            </b-button>
          </div>
          <p class="text-right">
            <a-button
              v-b-modal.terms-modal
              type="link"
              href="#"
            >
              {{ $t('auction.terms_link') }}
            </a-button>
          </p>
          <b-modal
            id="terms-modal"
            size="xl"
            hide-footer
            :title="$t('auction.terms_header')"
          >
            {{ $l(terms) }}
          </b-modal>
        </div>

        <a-divider />
        <transition name="fade">
          <a-table
            class="mb-3"
            :columns="columns"
            :data-source="auctionContent.bidList"
            :row-key="'id'"
            :pagination="false"
            :loading="loading"
            bordered
          >
            <template
              slot="date"
              slot-scope="text"
            >
              {{ text | dateFormatter('DD-MM-YY HH:mm') }}
            </template>
            <template
              slot="participant"
              slot-scope="name, record"
            >
              <p class="mb-1">
                {{ name }}
              </p>
              <p
                class="mb-0"
              >
                {{ record.sellerCompany }}
              </p>
            </template>
            <template
              slot="price"
              slot-scope="text"
            >
              {{ text | money }}
            </template>
          </a-table>
        </transition>
        <div
          v-if="user.role === 'ADMIN'"
          class="d-flex justify-content-end"
        >
          <b-button
            v-b-modal.confirm-delete
            variant="outline-danger"
          >
            {{ $t('common.delete') }}
          </b-button>
          <b-modal
            id="confirm-delete"
            centered
            hide-header
            :cancel-title="$t('common.cancel')"
            :ok-title="$t('common.confirm')"
            @ok="handleDeleteAuction"
          >
            {{ $t('auction.confirm_delete') }}
          </b-modal>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'

import terms from '@/components/auction/auction-terms'

export default {
  name: 'ActiveAuctionView',
  props: {
    categories: {
      type: Array,
      default: () => {},
    },
    settings: {
      type: Object,
      default: () => {},
    },

    auctionContent: {
      type: Object,
      default: () => {},
    },

    loading: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      expiryText: '',
      expiryTimer: null,
      sellerBid: null,
      terms: terms,
      auctionTypes: [
        {
          title: this.$t('auction.lot_types.main'),
          value: 'MAIN',
        },
        {
          title: this.$t('auction.lot_types.search'),
          value: 'SEARCH',
        },
        {
          title: this.$t('auction.lot_types.category'),
          value: 'CATEGORY',
        },
      ],

      columns: [
        {
          title: this.$t('auction.bids_log'),
          children: [
            {
              title: this.$t('common.date'),
              dataIndex: 'date_updated',
              scopedSlots: {
                customRender: 'date',
              },
            },
            {
              title: this.$t('auction.participant'),
              dataIndex: 'sellerFullName',
              scopedSlots: {
                customRender: 'participant',
              },
            },
            {
              title: this.$t('auction.bid_price'),
              dataIndex: 'price',
              scopedSlots: {
                customRender: 'price',
              },
            },
          ],
        },
      ],
    }
  },

  computed: {
    ...mapGetters({
      user: 'user/user',
    }),

    isStarted() {
      return this.auctionContent.status === 'STARTED'
    },

    sectionsTree() {
      const res = []
      this.categories.forEach(category => {
        const subCategories = []
        category.children.forEach(subCat => {
          const sections = subCat.children.filter(section => {
            return this.auctionContent.auctionCategories.includes(Number(section.key.split('_')[1]))
          })

          if (sections.length > 0) {
            subCategories.push({ ...subCat, children: sections })
          }
        })

        if (subCategories.length > 0) {
          res.push({ ...category, children: subCategories })
        }
      })

      return res
    },

    lastBidPrice() {
      return this.auctionContent.bidList[0] ? this.auctionContent.bidList[0].price : this.settings.startPrice
    },
  },

  watch: {
    lastBidPrice(newVal) {
      this.sellerBid = (newVal + this.settings.minimumStep) / 100
    },
  },

  mounted() {
    this.expiryCountdownStart()
    this.sellerBid = (this.lastBidPrice + this.settings.minimumStep) / 100
  },

  beforeDestroy() {
    clearInterval(this.expiryTimer)
  },

  methods: {
    expiryCountdownStart() {
      const duration = require('dayjs/plugin/duration')
      dayjs.extend(duration)
      const calculateInterval = () => {
        const now = dayjs().valueOf()
        const interval = this.settings.activeTo - now
        return dayjs.duration(interval).format(`D [${this.$t('common.days')}], H [${this.$t('common.hours')}], m [${this.$t('common.minutes')}], s [${this.$t('common.seconds')}]`)
      }

      this.expiryText = calculateInterval()
      this.expiryTimer = setInterval(() => {
        this.expiryText = calculateInterval()
      }, 1000)
    },

    getAuctionTypeTitle(code) {
      return this.auctionTypes.find(type => type.value === code)?.title
    },

    bidFormatter(value) {
      const floorValue = Math.round(Number(value) / (this.settings.minimumStep / 100)) * (this.settings.minimumStep / 100)
      const minBid = (this.lastBidPrice + this.settings.minimumStep) / 100
      return floorValue < minBid ? minBid : floorValue
    },

    makeBid() {
      this.$emit('makeBid', this.sellerBid)
    },

    handleDeleteAuction() {
      this.$emit('delete')
      this.$router.go()
    },
  },
}
</script>

<style scoped>
  .fade-enter-active {
  transition: opacity .2s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
</style>
